import React from 'react'
// hooks
import { useSnackbar } from 'notistack'
import { useFileUpload, isAbortError, UploadOptions } from 'hooks/useFileUpload'
// types
import { AssetType } from 'types/graphql'
// utils
import { fileToBase64 } from 'utils/upload'

export type Image = { name: string; url: string }

export const useImageUpload = ({
  onUpload,
  assetType,
  entityPrefix
}: {
  onUpload?: (image: Image | any) => void
  assetType: AssetType
  entityPrefix: string
}) => {
  const [image, setImage] = React.useState<Image | null>(null)

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { upload } = useFileUpload()

  // with onProgress the caller renders the progress itself, so the snackbar is skipped
  const uploadFile = async (file?: File, options: UploadOptions = {}) => {
    if (!file) {
      return
    }

    const withSnackbar = !options.onProgress

    try {
      if (withSnackbar) {
        enqueueSnackbar('Uploading...', {
          key: 'upload',
          variant: 'progress',
          autoHideDuration: null
        })
      }

      const isMsg = file.type === 'application/vnd.ms-outlook' || file.name.toLowerCase().endsWith('.msg')
      const contentType = isMsg ? 'application/vnd.ms-outlook' : file.type

      if (!navigator.onLine) {
        const base64file = await fileToBase64(file)

        onUpload?.({
          assetType,
          contentType,
          entityPrefix,
          file: base64file,
          fileName: file.name
        })

        return
      }

      const downloadUrl = await upload(
        file,
        {
          file: {
            assetType,
            contentType,
            entityPrefix,
            originalFileName: file.name
          }
        },
        options
      )

      const image = { name: file.name, url: downloadUrl }

      setImage(image)
      onUpload?.(image)
    } catch (err) {
      if (!isAbortError(err)) {
        console.error('[useImageUpload.uploadFile]', err)
      }
    } finally {
      // the 'upload' key is shared: closing it without opening would hide another component's snackbar
      if (withSnackbar) {
        closeSnackbar('upload')
      }
    }
  }

  const onFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation()
    event.preventDefault()

    await uploadFile(event.target.files[0])

    event.target.value = ''
  }

  return { image, onFileUpload, uploadFile }
}
